import { useMemo } from 'react';
import { useTestimonials, useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../utils/seo';
import { optimizeImageUrl, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import './Community.css';

const Community = () => {
  const { testimonials } = useTestimonials();
  const { settings } = useSiteSettings();
  const brand = settings.site_brand_name || 'Invincible NGO';
  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const pageTitle = settings.community_hero_title || 'Our Community';
  const pageSubtitle = settings.community_hero_subtitle || 'Stories from our adventure family';
  const sectionTitle = settings.community_section_title || 'What Our Community Says';

  const communitySchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Our Community', url: '/community' }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: `${pageTitle} | ${brand}`,
    description: pageSubtitle || settings.site_meta_description,
    canonical: publicBase ? `${publicBase}/community` : undefined,
    schema: communitySchema
  });


  return (
    <div className="community-page">
      <div className="page-header">
        <h1 className="page-title">{pageTitle}</h1>
        <p className="page-subtitle">{pageSubtitle}</p>
      </div>

      <div className="testimonials-section">
        <h2 className="section-title">{sectionTitle}</h2>
        <div className="testimonials-grid">
          {testimonials.length > 0 ? (
            testimonials.map((testimonial) => {
              const formattedDate = testimonial.createdAt
                ? new Date(testimonial.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })
                : 'Recently';

              return (
                <div key={testimonial.id} className="testimonial-card">
                  <div className="testimonial-header">
                    <img
                      src={optimizeImageUrl(
                        testimonial.imageUrl ||
                          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
                        { width: IMAGE_DISPLAY_WIDTH.thumb }
                      )}
                      alt={testimonial.name}
                      className="testimonial-avatar"
                      loading="lazy"
                    />
                    <div>
                      <h3>{testimonial.name}</h3>
                      <p>{formattedDate}</p>
                    </div>
                  </div>
                  <div
                    className="testimonial-rating"
                    aria-label={`${testimonial.rating || 5} out of 5 stars`}
                  >
                    {[1, 2, 3, 4, 5].map((i) => {
                      const filled = i <= (testimonial.rating ?? 5);
                      return (
                        <svg
                          key={i}
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill={filled ? 'currentColor' : 'none'}
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="testimonial-star"
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      );
                    })}
                  </div>
                  <p className="testimonial-text">{testimonial.quote}</p>
                </div>
              );
            })
          ) : (
            <p className="empty-state">No testimonials yet. Be the first to share your story!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Community;
